import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Toast from './Toast';

export default function Enquiries() {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState(null);
  const [templateModal, setTemplateModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [notesText, setNotesText] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/enquiries');
      const data = await response.json();
      setEnquiries(data);
      if (data.length > 0) {
        setSelectedId(data[0]._id);
        setNotesText(data[0].notes || '');
      }
    } catch (error) {
      console.error('Failed to fetch enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedId) return;
    try {
      const response = await fetch(`/api/enquiries/${selectedId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesText })
      });
      const updated = await response.json();
      setEnquiries(enquiries.map(e => e._id === selectedId ? updated : e));
      showToast('Notes saved');
    } catch (error) {
      showToast('Error saving notes');
    }
  };

  const templates = [
    'Thank you for reaching out. Let me connect you with our solutions team.',
    'I\'d love to learn more about your needs. Can we schedule a brief call next week?',
    'We have some case studies that might be relevant to your situation. Let me send those over.',
    'Thanks for your interest. I\'ll follow up with detailed pricing and implementation timeline.'
  ];

  const enquiry = enquiries.find(e => e._id === selectedId);
  const filteredEnquiries = enquiries.filter(e => {
    if (activeTab === 'all') return true;
    if (activeTab === 'mine') return e.createdAt;
    if (activeTab === 'qualified') return e.status === 'qualified';
    if (activeTab === 'cold') return e.status === 'cold';
    return true;
  });

  const showToast = (msg) => {
    setToast({ message: msg, type: 'default' });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    try {
      const response = await fetch(`/api/enquiries/${selectedId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: replyText })
      });
      const updated = await response.json();
      setEnquiries(enquiries.map(e => e._id === selectedId ? updated : e));
      showToast('Reply sent');
      setReplyText('');
    } catch (error) {
      showToast('Error sending reply');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedId) return;
    try {
      const response = await fetch(`/api/enquiries/${selectedId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const updated = await response.json();
      setEnquiries(enquiries.map(e => e._id === selectedId ? updated : e));
      showToast(`Status updated to ${newStatus}`);
    } catch (error) {
      showToast('Error updating status');
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      const response = await fetch(`/api/enquiries/${selectedId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        const newEnquiries = enquiries.filter(e => e._id !== selectedId);
        setEnquiries(newEnquiries);
        setSelectedId(newEnquiries.length > 0 ? newEnquiries[0]._id : null);
        showToast('Enquiry deleted');
      }
    } catch (error) {
      showToast('Error deleting enquiry');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Enquiries</h1>
          <div className="admin-page__sub">
            <span>{enquiries.length} pending</span>
            <span>{enquiries.filter(e => e.status === 'qualified').length} qualified</span>
            <span>Last reply · just now</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
        {['all', 'mine', 'qualified', 'cold'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              color: activeTab === tab ? 'var(--ivory)' : 'var(--graphite-2)',
              background: activeTab === tab ? 'rgba(244,241,234,0.05)' : 'none',
              border: activeTab === tab ? '1px solid rgba(244,241,234,0.1)' : '1px solid transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            {tab === 'all' && 'All'}
            {tab === 'mine' && 'Mine'}
            {tab === 'qualified' && 'Qualified'}
            {tab === 'cold' && 'Cold'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--graphite-2)' }}>
          Loading enquiries...
        </div>
      ) : !enquiry ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--graphite-2)' }}>
          No enquiries found
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px', minHeight: '600px' }}>
          <div style={{ borderRight: '1px solid rgba(244,241,234,0.08)', paddingRight: '24px', overflowY: 'auto', maxHeight: '80vh' }}>
            {filteredEnquiries.map(item => (
              <div
                key={item._id}
                onClick={() => {
                  setSelectedId(item._id);
                  setNotesText(item.notes || '');
                }}
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: selectedId === item._id ? 'rgba(244,241,234,0.05)' : 'transparent',
                  border: selectedId === item._id ? '1px solid rgba(244,241,234,0.1)' : '1px solid transparent',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--ivory)' }}>{item.name}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', backgroundColor: item.status === 'qualified' ? 'rgba(139,42,31,0.2)' : 'rgba(139,100,80,0.2)', color: item.status === 'qualified' ? '#E8A5A5' : '#D4B5A0', borderRadius: '2px' }}>
                    {item.status?.toUpperCase()}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--graphite-2)', marginBottom: '6px' }}>{item.company}</div>
                <div style={{ fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '4px', lineHeight: 1.3 }}>{item.message?.substring(0, 50)}</div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ paddingBottom: '16px', borderBottom: '1px solid rgba(244,241,234,0.08)', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--ivory)', marginBottom: '4px' }}>{enquiry.name}</h2>
                  <div style={{ fontSize: '13px', color: 'var(--graphite-2)' }}>{enquiry.email}</div>
                </div>
                <button
                  onClick={handleDelete}
                  style={{ fontSize: '14px', background: 'none', border: 'none', color: '#E85D5D', cursor: 'pointer', padding: '8px' }}
                  title="Delete enquiry"
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '12px', marginBottom: '16px' }}>
                <div>
                  <span style={{ color: 'var(--graphite-2)' }}>Company</span>
                  <div style={{ color: 'var(--ivory)', marginTop: '2px' }}>{enquiry.company}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--graphite-2)' }}>Industry</span>
                  <div style={{ color: 'var(--ivory)', marginTop: '2px' }}>{enquiry.industry || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--graphite-2)' }}>Interest</span>
                  <div style={{ color: 'var(--ivory)', marginTop: '2px' }}>{enquiry.projectInterest || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--graphite-2)' }}>Status</span>
                  <select
                    value={enquiry.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    style={{
                      marginTop: '2px',
                      padding: '6px',
                      background: 'rgba(244,241,234,0.05)',
                      border: '1px solid rgba(244,241,234,0.1)',
                      color: 'var(--ivory)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    <option value="cold">Cold</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px', maxHeight: '300px', overflowY: 'auto' }}>
              {enquiry.messages && enquiry.messages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    borderRadius: '6px',
                    background: msg.type === 'in' ? 'rgba(244,241,234,0.03)' : 'rgba(139,42,31,0.1)',
                    borderLeft: `2px solid ${msg.type === 'in' ? 'var(--graphite-2)' : 'var(--accent)'}`
                  }}
                >
                  <div style={{ fontSize: '13px', color: 'var(--ivory)', lineHeight: 1.5 }}>{msg.text}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', borderTop: '1px solid rgba(244,241,234,0.08)', paddingTop: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '8px' }}>Internal Notes</label>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Add internal notes about this enquiry..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '13px',
                    color: 'var(--ivory)',
                    background: 'rgba(244,241,234,0.02)',
                    border: '1px solid rgba(244,241,234,0.08)',
                    borderRadius: '6px',
                    fontFamily: 'inherit',
                    minHeight: '140px',
                    resize: 'vertical'
                  }}
                />
                <button
                  onClick={handleSaveNotes}
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: 'rgba(244,241,234,0.1)',
                    color: 'var(--ivory)',
                    border: '1px solid rgba(244,241,234,0.08)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Save notes
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {templateModal && (
        <Modal onClose={() => setTemplateModal(false)}>
          <h3 style={{ marginBottom: '16px', color: 'var(--ivory)', fontSize: '16px', fontWeight: 600 }}>Reply templates</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {templates.map((tpl, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setReplyText(tpl);
                  setTemplateModal(false);
                }}
                style={{
                  padding: '12px',
                  background: 'rgba(244,241,234,0.03)',
                  border: '1px solid rgba(244,241,234,0.08)',
                  borderRadius: '4px',
                  color: 'var(--ivory)',
                  fontSize: '13px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  lineHeight: 1.4,
                  transition: 'all 0.15s'
                }}
              >
                {tpl}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
